import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { formatoCantidad } from '@/lib/utils/formato-precio';
import { pricingRulesApi } from '@/features/pricing-rules/services/pricing-rules-api';
import type { ConteosDeReglas } from '@/features/pricing-rules/types/pricing-rules.types';
import { NuevaEstrategiaDialog } from './nueva-estrategia/nueva-estrategia-dialog';
import { usePuedeCrearEstrategias } from './nueva-estrategia/puede-crear';
import { atributosSoloMira, useSoporteSoloMira } from '@/features/mini-prixia/soporte-solo-mira';

/**
 * El día uno (plan 3, Tarea 7; maquetas `E-DiaUno` y `E-Metodos-Datos`). Un
 * cliente que sólo cargó su catálogo no tiene costo, ni competencia, ni ventas,
 * ni (casi) marca: es el momento en que decide si el producto le sirve. No es un
 * «onboarding» que se descarta: es el estado real de Hoy mientras falten datos.
 *
 * La cobertura sale de `GET /api/pricing-rules/conteos` → `catalogo` (4B-datos
 * T5, con `conVentas` y `conPrecioDeLista` de esta tarea). Esa ruta pide el
 * permiso de ver reglas (4B-datos T1b, desde el 26-sep): quien no lo tiene recibe
 * 403 y el panel no aparece.
 */
type Dato = { clave: string; titulo: string; hay: number | null; queCargar: string };

export function DiaUnoPanel({
  onVisible,
  hayEstrategiasActivas = false,
}: {
  /** Avisa si el panel está a la vista (Hoy esconde su propio «Crear una estrategia»). */
  onVisible?: (visible: boolean) => void;
  /**
   * Revisión final del plan 3, M5: con estrategias vivas ya no es el día uno
   * (el resumen de Hoy lo sabe): el panel no aparece.
   */
  hayEstrategiasActivas?: boolean;
} = {}) {
  const [catalogo, setCatalogo] = useState<ConteosDeReglas['catalogo'] | null>(null);
  const [creando, setCreando] = useState(false);
  // D5 (27-sep): crear pide un rol que pueda (el alta da 403 a los demás).
  const puedeCrear = usePuedeCrearEstrategias();
  // Soporte («Entrar como») en una demo sólo mira: no crea estrategias.
  const sinCambios = atributosSoloMira(useSoporteSoloMira());

  useEffect(() => {
    let vivo = true;
    pricingRulesApi
      .conteos(7)
      .then((c) => vivo && setCatalogo(c.catalogo))
      .catch(() => vivo && setCatalogo(null));
    return () => {
      vivo = false;
    };
  }, []);

  const visible =
    !hayEstrategiasActivas &&
    !!catalogo &&
    catalogo.total > 0 &&
    catalogo.conCosto === 0 &&
    catalogo.conCompetencia === 0;

  useEffect(() => {
    onVisible?.(visible);
  }, [visible, onVisible]);
  useEffect(() => () => onVisible?.(false), [onVisible]);

  if (!catalogo || !visible) return null;

  const datos: Dato[] = [
    {
      clave: 'costo',
      titulo: 'Costo',
      hay: catalogo.conCosto,
      queCargar:
        'Cargá el costo en Precios Dinámicos (a mano o por archivo) para apuntar a un margen.',
    },
    {
      clave: 'competencia',
      titulo: 'Competencia',
      hay: catalogo.conCompetencia,
      queCargar: 'Vinculá productos de la competencia para igualar o quedar por debajo.',
    },
    {
      clave: 'ventas',
      titulo: 'Ventas',
      hay: catalogo.conVentas ?? null,
      queCargar: 'Cargá el historial de ventas para que el precio inteligente mire la demanda.',
    },
    {
      clave: 'marca',
      titulo: 'Marca',
      hay: catalogo.conMarca,
      queCargar: 'Con la marca cargada se arman estrategias y reglas por marca.',
    },
  ];
  const conLista = catalogo.conPrecioDeLista ?? null;
  const num = formatoCantidad;

  return (
    <section
      aria-label="Tu primer día"
      data-testid="dia-uno"
      className="space-y-3 rounded-lg border p-4"
    >
      <div>
        <div className="font-semibold">Lo que ya podés hacer con tu catálogo</div>
        <p className="text-sm text-muted-foreground">
          Tenés {num(catalogo.total)} productos cargados. Todavía faltan datos para casi todo, pero
          hay una estrategia que no los necesita.
        </p>
      </div>
      <div className="grid grid-cols-4 gap-3">
        {datos.map((d) => (
          <div
            key={d.clave}
            data-testid={`dato-${d.clave}`}
            className={cn('rounded-md p-3 text-sm', d.hay ? 'border' : 'border border-dashed')}
          >
            <div className="font-medium">{d.titulo}</div>
            <div className="tabular-nums">
              {d.hay == null ? '—' : `${num(d.hay)} de ${num(catalogo.total)}`}
            </div>
            {!d.hay && <p className="mt-1 text-xs text-muted-foreground">{d.queCargar}</p>}
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between gap-3 rounded-md border border-info-borde bg-info-suave p-3 text-sm">
        <div>
          <div className="font-medium">Bajar un % de mi precio de lista</div>
          <p className="text-xs text-muted-foreground">
            No necesita costo ni competencia.{' '}
            {conLista == null
              ? ''
              : conLista === 0
                ? 'Ningún producto tiene precio de lista: cargalo para poder usarla.'
                : `La pueden usar ${num(conLista)} de ${num(catalogo.total)} productos (los que tienen precio de lista).`}
          </p>
        </div>
        {puedeCrear ? (
          <Button disabled={conLista === 0} onClick={() => setCreando(true)} {...sinCambios}>
            Crear una estrategia
          </Button>
        ) : (
          <p className="max-w-48 text-xs text-muted-foreground">
            La crea quien administra la empresa o aprueba precios.
          </p>
        )}
      </div>
      <NuevaEstrategiaDialog
        abierto={creando}
        onCerrar={() => setCreando(false)}
        onCreada={() => setCreando(false)}
        inicial={{ metodo: 'DISCOUNT_PERCENT' }}
      />
    </section>
  );
}
